import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  DATE_FORMATS,
  REGEX,
  TOY_RESERVATION_DATE_OPERATORS,
  TOY_RESERVATION_DATE_UNITS,
  TOY_RESERVATION_RULE_TYPES,
  TOY_RESERVATION_TAG_LOGIC,
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import ModalComponent from '../../../../../components/ModalComponent';
import NumberComponent from '../../../../../components/NumberComponent';
import { buildRuleFormValues } from '../toyReservationRuleUtils';
import PhoneNumbersInput from './PhoneNumbersInput';
import ReservationUserSelect from './ReservationUserSelect';
import ReservationUserTagSelect from './ReservationUserTagSelect';

const { Item } = Form;
const { RangePicker } = DatePicker;
const { required } = formValidatorRules;

const RULE_TYPE_OPTIONS = Object.values(TOY_RESERVATION_RULE_TYPES)?.map(
  (item) => ({ label: item?.label, value: item?.value }),
);

const DATE_OPERATOR_OPTIONS = Object.values(
  TOY_RESERVATION_DATE_OPERATORS,
)?.map((item) => ({ label: item?.label, value: item?.value }));

const TAG_LOGIC_OPTIONS = Object.values(TOY_RESERVATION_TAG_LOGIC)?.map(
  (item) => ({ label: item?.label, value: item?.value }),
);

const getDateUnitOptions = (duration) =>
  Object.values(TOY_RESERVATION_DATE_UNITS)?.map((item) => ({
    label: Number(duration) === 1 ? item?.label : item?.pluralLabel,
    value: item?.value,
  }));

const MAX_WITHIN_LAST_DURATION = 999;

const MATCH_LOGIC_HINT =
  'AND requires all selected tags on a subscriber, OR requires any one.';

const PHONE_NUMBERS_HINT =
  'Type a phone number and press enter. Use this for people not found in the search above — as soon as they become a user, this rule covers them automatically.';

const ORDER_VALUE_HINT =
  'Set both minimum and maximum to 0 to reserve toy for new users only.';

const ORDER_COUNT_HINT =
  'Set both minimum and maximum to 0 to reserve toy for new users only.';

const labelWithHint = (label, hint) => (
  <span className="label-with-hint">
    {label}
    <Tooltip title={hint} placement="top">
      <InfoCircleOutlined className="pointer" />
    </Tooltip>
  </span>
);

const ConfigReservationRuleModal = ({
  isModalOpen,
  setIsModalOpen,
  onFinish,
  loadings,
  form,
  rule,
  onClose,
}) => {
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);
  const [ruleType, setRuleType] = useState(null);
  const [dateOperator, setDateOperator] = useState(null);
  const watchedUserIds = Form.useWatch('userIds', form);
  const watchedContactNos = Form.useWatch('contactNos', form);
  const watchedDuration = Form.useWatch('duration', form);

  const handleCancel = () => {
    setIsModalOpen(false);
    form?.resetFields();
    onClose?.();
  };

  useEffect(() => {
    if (!isModalOpen) return;

    if (rule?.id) {
      const values = buildRuleFormValues(rule);
      form?.setFieldsValue(values);
      setRuleType(values?.type);
      setDateOperator(values?.op);
    } else {
      form?.resetFields();
      form?.setFieldsValue({ isActive: true });
      setRuleType(null);
      setDateOperator(null);
    }
  }, [form, isModalOpen, rule]);

  const handleTypeChange = (value) => {
    setRuleType(value);
    setDateOperator(null);
    form?.setFieldsValue({
      op: undefined,
      date: undefined,
      dateRange: undefined,
      duration: undefined,
      unit: undefined,
      tagIds: undefined,
      logic: TOY_RESERVATION_TAG_LOGIC?.OR?.value,
      userIds: undefined,
      contactNos: undefined,
      min: undefined,
      max: undefined,
    });
  };

  const handleDateOperatorChange = (value) => {
    setDateOperator(value);
    form?.setFieldsValue({
      date: undefined,
      dateRange: undefined,
      duration: undefined,
      unit:
        value === TOY_RESERVATION_DATE_OPERATORS?.WITHIN_LAST?.value
          ? TOY_RESERVATION_DATE_UNITS?.DAY?.value
          : undefined,
    });
  };

  const isBetween =
    dateOperator === TOY_RESERVATION_DATE_OPERATORS?.BETWEEN?.value;

  const isWithinLast =
    dateOperator === TOY_RESERVATION_DATE_OPERATORS?.WITHIN_LAST?.value;

  const renderWithinLastFields = () => (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Item
          label="Duration"
          name="duration"
          rules={[
            { required: true, message: 'Please Enter Duration!' },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '') {
                  return Promise.resolve();
                }
                const duration = Number(value);
                if (!Number.isInteger(duration) || duration < 1) {
                  return Promise.reject(
                    new Error('Duration must be at least 1'),
                  );
                }
                if (duration > MAX_WITHIN_LAST_DURATION) {
                  return Promise.reject(
                    new Error(
                      `Duration must be ${MAX_WITHIN_LAST_DURATION} or less`,
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <NumberComponent
            allowClear
            min={1}
            placeholder="Enter Duration"
            isFractional={false}
          />
        </Item>
      </Col>

      <Col xs={24} sm={12}>
        <Item
          label="Unit"
          name="unit"
          rules={[{ required: true, message: 'Please Select Unit!' }]}
        >
          <Select
            placeholder="Select Unit"
            options={getDateUnitOptions(watchedDuration)}
          />
        </Item>
      </Col>
    </Row>
  );

  const renderAccountCreatedFields = () => (
    <>
      <Item
        label="Condition"
        name="op"
        rules={[{ required: true, message: 'Please Select Condition!' }]}
      >
        <Select
          placeholder="Select Condition"
          options={DATE_OPERATOR_OPTIONS}
          onChange={handleDateOperatorChange}
        />
      </Item>

      {isWithinLast && renderWithinLastFields()}

      {!isWithinLast &&
        (isBetween ? (
          <Item
            label="Account Created Between"
            name="dateRange"
            required
            rules={[
              {
                validator: (_, value) =>
                  value?.[0] && value?.[1]
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please Select Date Range!')),
              },
            ]}
          >
            <RangePicker
              className="full-width"
              format={DATE_FORMATS?.REGULAR}
            />
          </Item>
        ) : (
          <Item
            label="Account Created Date"
            name="date"
            rules={[{ required: true, message: 'Please Select Date!' }]}
          >
            <DatePicker className="full-width" format={DATE_FORMATS?.REGULAR} />
          </Item>
        ))}
    </>
  );

  const renderUserTagFields = () => (
    <>
      <Item
        label="Tags"
        name="tagIds"
        required
        rules={[
          {
            validator: (_, value) =>
              value?.length
                ? Promise.resolve()
                : Promise.reject(new Error('Please Select at least one Tag!')),
          },
        ]}
      >
        <ReservationUserTagSelect preloadTagIds={rule?.config?.tagIds || []} />
      </Item>

      <Item
        label={labelWithHint('Match Logic', MATCH_LOGIC_HINT)}
        name="logic"
        rules={[{ required: true, message: 'Please Select Match Logic!' }]}
      >
        <Radio.Group options={TAG_LOGIC_OPTIONS} optionType="button" />
      </Item>
    </>
  );

  const renderSpecificUserFields = () => {
    const hasUsers = watchedUserIds?.length > 0;
    const hasContactNos = watchedContactNos?.length > 0;

    return (
      <>
        <Item
          label="Users"
          name="userIds"
          required={!hasContactNos}
          dependencies={['contactNos']}
          rules={[
            {
              validator: (_, value) =>
                value?.length || form?.getFieldValue('contactNos')?.length
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('Please Select a User or add a Phone Number!'),
                    ),
            },
          ]}
        >
          <ReservationUserSelect preloadUserIds={rule?.config?.userIds || []} />
        </Item>

        <Item
          label={labelWithHint('Phone Numbers', PHONE_NUMBERS_HINT)}
          name="contactNos"
          required={!hasUsers}
          dependencies={['userIds']}
          rules={[
            {
              validator: (_, value) => {
                const invalid = (value || [])?.find(
                  (contactNo) => !REGEX?.MOBILE_NUMBER?.test(contactNo),
                );
                if (invalid) {
                  return Promise.reject(
                    new Error(`"${invalid}" is not a valid number`),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <PhoneNumbersInput />
        </Item>
      </>
    );
  };

  const renderRangeFields = ({ minLabel, maxLabel, hint, unit }) => (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Item
          label={labelWithHint(minLabel, hint)}
          name="min"
          rules={[
            { required: true, message: `Please Enter Minimum ${unit}!` },
          ]}
        >
          <NumberComponent
            allowClear
            placeholder={`Enter Minimum ${unit}`}
            isFractional={false}
          />
        </Item>
      </Col>

      <Col xs={24} sm={12}>
        <Item
          label={labelWithHint(maxLabel, hint)}
          name="max"
          dependencies={['min']}
          rules={[
            { required: true, message: `Please Enter Maximum ${unit}!` },
            {
              validator: (_, value) => {
                const min = form?.getFieldValue('min');
                if (
                  value === undefined ||
                  value === null ||
                  value === '' ||
                  min === undefined ||
                  min === null ||
                  min === ''
                ) {
                  return Promise.resolve();
                }
                return Number(value) >= Number(min)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        'Maximum must be greater than or equal to Minimum',
                      ),
                    );
              },
            },
          ]}
        >
          <NumberComponent
            allowClear
            placeholder={`Enter Maximum ${unit}`}
            isFractional={false}
          />
        </Item>
      </Col>
    </Row>
  );

  const renderOrderValueFields = () =>
    renderRangeFields({
      minLabel: 'Minimum Order Value (Rs)',
      maxLabel: 'Maximum Order Value (Rs)',
      hint: ORDER_VALUE_HINT,
      unit: 'Value',
    });

  const renderOrderCountFields = () =>
    renderRangeFields({
      minLabel: 'Minimum Number of Orders',
      maxLabel: 'Maximum Number of Orders',
      hint: ORDER_COUNT_HINT,
      unit: 'Number of Orders',
    });

  const renderConfigFields = () => {
    switch (ruleType) {
      case TOY_RESERVATION_RULE_TYPES?.ACCOUNT_CREATED?.value:
        return renderAccountCreatedFields();
      case TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value:
        return renderUserTagFields();
      case TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value:
        return renderSpecificUserFields();
      case TOY_RESERVATION_RULE_TYPES?.ORDER_VALUE?.value:
        return renderOrderValueFields();
      case TOY_RESERVATION_RULE_TYPES?.ORDER_COUNT?.value:
        return renderOrderCountFields();
      default:
        return null;
    }
  };

  return (
    <ModalComponent
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={form?.submit}
      title={rule?.id ? 'Edit Reservation Rule' : 'Add Reservation Rule'}
      okText="Save"
      confirmLoading={loadings}
      destroyOnClose
      forceRender
      maskClosable={false}
    >
      <Divider />
      <Form
        form={form}
        onFinish={(values) => {
          setIsValidateTrigger(true);
          onFinish(values);
        }}
        autoComplete="off"
        layout="vertical"
        validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
        onFinishFailed={() => setIsValidateTrigger(true)}
      >
        <Item
          label="Rule Name"
          name="name"
          rules={[{ ...required, message: 'Please Enter Rule Name!' }]}
        >
          <Input allowClear placeholder="Enter Rule Name" maxLength={100} />
        </Item>

        <Item
          label="Rule Type"
          name="type"
          rules={[{ required: true, message: 'Please Select Rule Type!' }]}
        >
          <Select
            placeholder="Select Rule Type"
            options={RULE_TYPE_OPTIONS}
            onChange={handleTypeChange}
          />
        </Item>

        {renderConfigFields()}

        <Item label="Active" name="isActive" valuePropName="checked">
          <Switch size="small" />
        </Item>
      </Form>
    </ModalComponent>
  );
};

export default ConfigReservationRuleModal;
